import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { submitKieTask, pollKieTask, getKieApiKey, getKieCallbackUrl } from "@/lib/kie-service";

type GenerationStatus = "pending" | "processing" | "completed" | "failed";

interface GenerationResult {
  success: boolean;
  logId?: string;
  resultUrl?: string;
  error?: string;
  message?: string;
}

// Hidden backend prompt templates for professional mode
const PROFESSIONAL_PROMPTS = {
  sora2_15s: "Create a cinematic masterpiece with professional-grade quality. [USER_OPTIONS] Use advanced lighting, perfect composition, and film-standard color grading. Ensure smooth camera movements and Hollywood-level production value."
};

interface VipOptions {
  cameraAngle?: string;
  scene?: string;
  mood?: string;
  style?: string;
  aspectRatio?: string;
  character?: string;
  additionalScript?: string;
  skipPolling?: boolean;
}

export const useGenerationVip = () => {
  const { user, profile, refreshProfile } = useAuth();
  const { t } = useLanguage();
  const [isGenerating, setIsGenerating] = useState(false);

  const checkCredits = (requiredCredits: number): boolean => {
    if (!profile) {
      toast.error(t("กรุณาเข้าสู่ระบบ", "Please log in"));
      return false;
    }

    if (profile.credits < requiredCredits) {
      toast.error(
        t(
          `เครดิตไม่เพียงพอ ต้องการ ${requiredCredits} เครดิต คุณมี ${profile.credits} เครดิต`,
          `Insufficient credits. Need ${requiredCredits}, you have ${profile.credits}`
        )
      );
      return false;
    }

    return true;
  };

  const deductCredits = async (amount: number): Promise<boolean> => {
    if (!user || !profile) return false;

    const newCredits = profile.credits - amount;

    const { error } = await supabase
      .from("profiles")
      .update({ credits: newCredits })
      .eq("user_id", user.id);

    if (error) {
      console.error("Error deducting credits:", error);
      toast.error(t("เกิดข้อผิดพลาดในการหักเครดิต", "Error deducting credits"));
      return false;
    }

    return true;
  };

  const refundCredits = async (amount: number): Promise<boolean> => {
    if (!user || !profile) return false;

    const newCredits = (profile.credits || 0) + amount;

    const { error } = await supabase
      .from("profiles")
      .update({ credits: newCredits })
      .eq("user_id", user.id);

    if (error) {
      console.error("Error refunding credits:", error);
      return false;
    }

    await refreshProfile();
    return true;
  };

  const buildProfessionalPrompt = (
    templateTitle: string,
    templatePrompt: string,
    options: VipOptions
  ): { displayPrompt: string; actualPrompt: string } => {
    // Build the display prompt (what user sees)
    const displayParts = [`Video Style: ${templateTitle}`];
    if (options.cameraAngle) displayParts.push(`Camera: ${options.cameraAngle}`);
    if (options.scene) displayParts.push(`Scene: ${options.scene}`);
    if (options.mood) displayParts.push(`Mood: ${options.mood}`);
    if (options.style) displayParts.push(`Style: ${options.style}`);
    if (options.character) displayParts.push(`Character: ${options.character}`);
    if (options.additionalScript) displayParts.push(`Script: ${options.additionalScript}`);

    const displayPrompt = `Professional Mode\n${displayParts.join(" | ")}`;

    // Build the actual prompt (what gets sent to AI) - use the template's prompt directly
    // Replace #character with actual character value if provided
    let actualPrompt = templatePrompt;
    if (options.character) {
      actualPrompt = actualPrompt.replace(/#character/gi, options.character);
    }

    const optionParts = [];
    if (options.cameraAngle) optionParts.push(options.cameraAngle);
    if (options.scene) optionParts.push(options.scene);
    if (options.mood) optionParts.push(options.mood);
    if (options.style) optionParts.push(options.style);

    if (optionParts.length > 0) {
      const userOptions = optionParts.join(", ");
      // If template has [USER_OPTIONS] placeholder, replace it
      if (actualPrompt.includes("[USER_OPTIONS]")) {
        actualPrompt = actualPrompt.replace("[USER_OPTIONS]", userOptions);
      } else {
        // Otherwise append to the prompt
        actualPrompt = `${actualPrompt}. ${userOptions}.`;
      }
    }

    // Append additional script at the end if provided
    if (options.additionalScript) {
      actualPrompt = `${actualPrompt} Additional scene details: ${options.additionalScript}`;
    }

    return { displayPrompt, actualPrompt };
  };

  const createLog = async (
    model: string,
    prompt: string,
    displayPrompt: string,
    creditsUsed: number
  ): Promise<string | null> => {
    if (!user) return null;

    const { data, error } = await supabase
      .from("generation_logs")
      .insert({
        user_id: user.id,
        service_type: "video",
        model,
        prompt,
        display_prompt: displayPrompt,
        credits_used: creditsUsed,
        is_professional: true,
        status: "pending" as GenerationStatus,
      })
      .select("id")
      .single();

    if (error) {
      console.error("Error creating log:", error);
      return null;
    }

    return data.id;
  };

  const updateLogStatus = async (
    logId: string,
    status: GenerationStatus,
    resultUrl?: string,
    errorMessage?: string
  ): Promise<boolean> => {
    console.log('updateLogStatus called:', { logId, status, errorMessage });

    if (!user) {
      console.error("No user session for update!");
      return false;
    }

    const { data: sessionData } = await supabase.auth.getSession();

    if (!sessionData?.session) {
      console.error("No active session!");
      return false;
    }

    const updateData: { status: GenerationStatus; result_url?: string; completed_at?: string; error_message?: string } = {
      status,
    };

    if (resultUrl) {
      updateData.result_url = resultUrl;
    }

    if (status === "completed" || status === "failed") {
      updateData.completed_at = new Date().toISOString();
    }

    if (status === "failed" && errorMessage) {
      updateData.error_message = errorMessage;
    }

    const { data, error } = await supabase
      .from("generation_logs")
      .update(updateData)
      .eq("id", logId)
      .eq("user_id", user.id)
      .select();

    if (error) {
      console.error("Error updating log:", error);
      return false;
    }

    if (!data || data.length === 0) {
      console.warn("RLS blocked update! Trying RPC function...");

      const completedAt = (status === "completed" || status === "failed")
        ? new Date().toISOString()
        : null;

      const { data: rpcResult, error: rpcError } = await supabase.rpc('update_generation_log_status' as any, {
        p_log_id: logId,
        p_user_id: user.id,
        p_status: status,
        p_result_url: resultUrl || null,
        p_completed_at: completedAt,
        p_error_message: errorMessage || null
      });

      if (rpcError) {
        console.error("RPC update failed:", rpcError.message);
        return false;
      }

      return rpcResult === true;
    }

    return true;
  };

  const generate = async (
    templateTitle: string,
    templatePrompt: string,
    creditsRequired: number,
    options: VipOptions
  ): Promise<GenerationResult> => {
    if (!user || !profile) {
      return { success: false, error: "Not authenticated" };
    }

    if (!checkCredits(creditsRequired)) {
      return { success: false, error: "Insufficient credits" };
    }

    const apiKey = await getKieApiKey();
    if (!apiKey) {
      toast.error(t("กรุณาตั้งค่า API Key ในหน้า Admin", "Please configure API Key in Admin page"));
      return { success: false, error: "Missing API Key" };
    }

    setIsGenerating(true);

    let logId: string | null = null;
    try {
      // Build prompts (hidden + display)
      const { displayPrompt, actualPrompt } = buildProfessionalPrompt(templateTitle, templatePrompt, options);

      // 1. Create log entry
      logId = await createLog("Seedance 2.0 Fast (15s) Professional", actualPrompt, displayPrompt, creditsRequired);
      // console.log('Created Log Entry:', logId);
      if (!logId) {
        setIsGenerating(false);
        return { success: false, error: "Failed to create log" };
      }

      // 2. Update to processing
      const updatedToProcessing = await updateLogStatus(logId, "processing");
      if (!updatedToProcessing) {
        throw new Error("Failed to update log status to processing");
      }

      // 3. Deduct credits
      const deducted = await deductCredits(creditsRequired);
      if (!deducted) {
        console.warn('Credit deduction failed');
        await updateLogStatus(logId, "failed");
        setIsGenerating(false);
        return { success: false, error: "Failed to deduct credits" };
      }

      // 4. Submit Task to Kie.ai for Seedance 2.0
      const callBackUrl = await getKieCallbackUrl();
      const body = {
        model: "bytedance/seedance-2-fast",
        input: {
          prompt: actualPrompt,
          generate_audio: true,
          resolution: "720p",
          aspect_ratio: options.aspectRatio || "9:16",
          duration: 15,
          web_search: false,
          nsfw_checker: true
        },
        ...(callBackUrl ? { callBackUrl } : {})
      };

      const submission = await submitKieTask("/api/v1/jobs/createTask", apiKey, body);
      // console.log('Kie.ai Submission Result:', submission);

      if (submission.code && submission.code !== 200 && submission.code !== 0) {
        throw new Error(submission.msg || submission.message || `API Error ${submission.code}`);
      }

      const taskId = submission.taskId ||
        submission.id ||
        (typeof submission.data === 'string' ? submission.data : (submission.data?.taskId || submission.data?.id));

      // console.log('Extracted Task ID:', taskId);

      if (!taskId) {
        console.error('Failed to extract taskId from:', submission);
        throw new Error(t("ไม่ได้รับ Task ID จากระบบ AI", "No taskId received from Kie.ai"));
      }

      // 5. Save taskId to database
      await updateLogStatus(logId, "processing", taskId);

      // Show temporary message
      toast.info(t(`🎬 โหมดมืออาชีพ: กำลังสร้างวิดีโอ... (ประมาณ 6 นาที)\n\nไปทำอย่างอื่นรอได้ครับ กลับมาเช็กทีหลัง`,
        `🎬 Professional Mode: Generating video... (~6 minutes)\n\nYou can do other things. Check back later on Dashboard`), { duration: 8000 });

      // If skipPolling is true, return early
      if (options.skipPolling) {
        setIsGenerating(false);
        return { success: true, logId };
      }

      // Polling for result
      let resultUrl = "";
      let attempts = 0;
      const maxAttempts = 120;
      const pollInterval = 15000;
      let kiePollAttempts = 0;

      // Wait before first poll
      // console.log('Video generation - waiting 30s before first poll...');
      await new Promise(resolve => setTimeout(resolve, 30000));

      while (attempts < maxAttempts) {
        // 1. Check our own DB first
        const { data: currentLog, error: dbError } = await supabase
          .from("generation_logs")
          .select("status, result_url")
          .eq("id", logId)
          .single();

        if (dbError) {
          console.warn('DB Poll Error:', dbError);
        }

        if (currentLog?.status === "completed" && currentLog?.result_url?.startsWith("http")) {
          // console.log('Found result in DB via callback!');
          resultUrl = currentLog.result_url;
          break;
        } else if (currentLog?.status === "failed") {
          throw new Error(t("การสร้างล้มเหลว (แจ้งจากระบบ)", "Generation failed (reported by system)"));
        }

        // 2. Poll Kie AI directly (max 3 times)
        const shouldPollKie = taskId && kiePollAttempts < 3 && (attempts === 0 || attempts % 10 === 0);

        if (shouldPollKie) {
          try {
            kiePollAttempts++;
            console.log(`Polling Kie AI (${kiePollAttempts}/3) - Attempt ${attempts}...`);

            const pollResult = await pollKieTask(taskId, apiKey, "market");
            // console.log('Kie API Response:', pollResult);

            const response = pollResult.data || pollResult;
            const resData = response.response || response.result;

            let kieUrl: string | null = null;
            let kieStatus = response.state ?? response.status ?? response.taskStatus;

            if (typeof kieStatus === 'number') {
              if (kieStatus === 3) kieStatus = "success";
              else if (kieStatus === 4) kieStatus = "failed";
            }

            if (resData && typeof resData === 'object') {
              kieUrl = resData.url || resData.video_url;
            }

            if (!kieUrl) {
              kieUrl = response.url || response.resultUrl || response.video_url;
            }

            if (!kieUrl && (pollResult.resultJson || response.resultJson)) {
              const rj = pollResult.resultJson || response.resultJson;
              try {
                const parsed = typeof rj === 'string' ? JSON.parse(rj) : rj;
                if (parsed?.url) kieUrl = parsed.url;
                if (!kieUrl && parsed?.resultUrls?.[0]) kieUrl = parsed.resultUrls[0];
                if (!kieUrl && parsed?.video_url) kieUrl = parsed.video_url;
                if (!kieUrl && parsed?.result?.url) kieUrl = parsed.result.url;
              } catch (e) {
                console.warn('resultJson parse error:', e);
              }
            }

            // console.log('kieUrl:', kieUrl, '| kieStatus:', kieStatus);

            // Check if task is successful
            const isSuccess = kieStatus === "success" ||
              kieStatus === "SUCCESS" ||
              kieStatus === "completed" ||
              kieStatus === "COMPLETED" ||
              kieStatus === 3;

            // If success status but no URL yet, try to extract from resultJson more aggressively
            if (isSuccess && !kieUrl && (pollResult.resultJson || response.resultJson)) {
              const rj = pollResult.resultJson || response.resultJson;
              // console.log('Success status detected, extracting URL from resultJson:', rj);
              try {
                const parsed = typeof rj === 'string' ? JSON.parse(rj) : rj;
                // Try multiple paths to find the URL
                kieUrl = parsed?.url ||
                  parsed?.resultUrls?.[0] ||
                  parsed?.video_url ||
                  parsed?.result?.url ||
                  parsed?.data?.url ||
                  parsed?.response?.url;
              } catch (e) {
                console.warn('resultJson parse error on success:', e);
              }
            }

            if (isSuccess && kieUrl) {
              // console.log('Found result via direct polling! Updating DB...');
              resultUrl = kieUrl;
              await updateLogStatus(logId, "completed", resultUrl);
              break;
            } else if (kieStatus === "failed" || kieStatus === "FAILED" || kieStatus === "error" || kieStatus === 4) {
              throw new Error(t("การสร้างล้มเหลว", "Generation failed"));
            }
          } catch (pollErr: any) {
            console.warn('Poll Error:', pollErr?.message || pollErr);
          }
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;
      }

      if (!resultUrl) throw new Error("Generation timed out");

      await updateLogStatus(logId, "completed", resultUrl);
      await refreshProfile();
      toast.success(t(`สร้างสำเร็จ! หัก ${creditsRequired} เครดิต (โหมดมืออาชีพ)`, `Generated successfully! ${creditsRequired} credits deducted (Professional Mode)`));

      setIsGenerating(false);
      return { success: true, logId, resultUrl };
    } catch (error: any) {
      console.error("Generation error:", error);
      setIsGenerating(false);

      // Extract error message for storage
      const rawErrorMessage = error.message || t("เกิดข้อผิดพลาดในการสร้าง", "Generation failed");

      if (creditsRequired > 0) {
        console.log(`Refunding ${creditsRequired} credits due to failure`);
        await refundCredits(creditsRequired);
      }

      if (logId) {
        await updateLogStatus(logId, "failed", undefined, rawErrorMessage);
      }

      // Translate common API errors to Thai for display
      let errorMessage = rawErrorMessage;

      if (error.message?.toLowerCase().includes("cameo") || error.message?.toLowerCase().includes("not allowed to access")) {
        errorMessage = t(
          "❌ ไม่สามารถเข้าถึงตัวละคร/Cameo ได้\n\nกรุณาตั้งค่าตัวละครเป็น Public ก่อนใช้งาน\n💰 เครดิตถูกคืนเรียบร้อยแล้ว",
          "❌ Cannot access character/Cameo\n\nPlease set your character to Public before using.\n💰 Credits have been refunded"
        );
      } else if (error.message?.toLowerCase().includes("insufficient") || error.message?.toLowerCase().includes("balance")) {
        errorMessage = t(
          "❌ ระบบ AI ทำงานผิดพลาด กรุณาแจ้ง Admin\n\n💰 เครดิตถูกคืนเรียบร้อยแล้ว",
          "❌ AI system error. Please contact Admin.\n\n💰 Credits have been refunded"
        );
      } else if (error.message?.toLowerCase().includes("rate limit") || error.message?.toLowerCase().includes("too many")) {
        errorMessage = t(
          "❌ ระบบ AI กำลังทำงานหนัก กรุณารอสักครู่แล้วลองใหม่\n\n💰 เครดิตถูกคืนเรียบร้อยแล้ว",
          "❌ AI system is busy. Please wait and try again.\n\n💰 Credits have been refunded"
        );
      } else if (creditsRequired > 0) {
        // Add refund notice for other errors
        errorMessage = `${errorMessage}\n\n💰 ${t("เครดิตถูกคืนเรียบร้อยแล้ว", "Credits have been refunded")}`;
      }

      toast.error(errorMessage, { duration: 8000 });
      return { success: false, error: error.message || "Generation failed" };
    }
  };

  return {
    generate,
    isGenerating,
    checkCredits,
    deductCredits,
    refundCredits,
    createLog,
    updateLogStatus,
    userCredits: profile?.credits ?? 0,
  };
};
